import * as React from "react"

import { cn } from "../../lib/utils"

// Context to share image loading state between AvatarImage and AvatarFallback
const AvatarContext = React.createContext<{
  imageLoaded: boolean
  setImageLoaded: (loaded: boolean) => void
}>({
  imageLoaded: false,
  setImageLoaded: () => {},
})

const Avatar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const [imageLoaded, setImageLoaded] = React.useState(false)

  return (
    <AvatarContext.Provider value={{ imageLoaded, setImageLoaded }}>
      <div
        ref={ref}
        className={cn(
          "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </AvatarContext.Provider>
  )
})
Avatar.displayName = "Avatar"

const AvatarImage = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement>
>(({ className, src, onLoad, onError, ...props }, ref) => {
  const { setImageLoaded } = React.useContext(AvatarContext)
  const [loadStatus, setLoadStatus] = React.useState<"loading" | "loaded" | "error">(
    src ? "loading" : "error"
  )

  // Reset load status when src changes
  React.useEffect(() => {
    if (src) {
      setLoadStatus("loading")
      setImageLoaded(false)
    } else {
      setLoadStatus("error")
      setImageLoaded(false)
    }
  }, [src, setImageLoaded])

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setLoadStatus("loaded")
    setImageLoaded(true)
    onLoad?.(e)
  }

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setLoadStatus("error")
    setImageLoaded(false)
    onError?.(e)
  }

  // Don't render if no src or if loading failed
  if (!src || loadStatus === "error") {
    return null
  }

  return (
    <img
      ref={ref}
      src={src}
      alt=""
      onLoad={handleLoad}
      onError={handleError}
      className={cn(
        "aspect-square h-full w-full object-cover",
        loadStatus === "loading" && "invisible",
        className
      )}
      {...props}
    />
  )
})
AvatarImage.displayName = "AvatarImage"

const AvatarFallback = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { imageLoaded } = React.useContext(AvatarContext)

  // Always render fallback behind the image - it shows when image isn't loaded
  return (
    <div
      ref={ref}
      className={cn(
        "absolute inset-0 flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground",
        imageLoaded && "invisible",
        className
      )}
      {...props}
    />
  )
})
AvatarFallback.displayName = "AvatarFallback"

export { Avatar, AvatarImage, AvatarFallback }
